import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import { authService, agentService } from "../../services/api";
import AgencySearchModal from "../agency/AgencySearchModal";
import {
  LICENSE_FILE_ACCEPT,
  pickValidLicenseFile,
} from "../../utils/licenseFile";
import toast from "react-hot-toast";

const AgentDetailsStep = () => {
  const dispatch = useDispatch();
  const { error, invitationContext } = useSelector((state) => state.signup);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    hasLicense: false,
    licenseNumber: "",
    licenseFile: null,
  });

  // Agency selection state
  const [showAgencySearchModal, setShowAgencySearchModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLicenseCheckboxChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      hasLicense: checked,
      licenseNumber: checked ? prev.licenseNumber : "",
      licenseFile: checked ? prev.licenseFile : null,
    }));
  };

  const handleFileChange = (event) => {
    // Shared 10MB + MIME check (src/utils/licenseFile.js) — same rule and
    // same toasts as the agency step and the "Update License" modal.
    const file = pickValidLicenseFile(event);
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      licenseFile: file,
    }));
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      licenseFile: null,
    }));
    // Reset the file input
    const fileInput = document.getElementById("license-file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleAgencySelect = (agency) => {
    setSelectedAgency(agency);
    toast.success(`Selected agency: ${agency.name}`);
  };

  const handleCreateAgencyAccount = () => {
    dispatch(setSignupData({ accountType: "agency" }));
    dispatch(setVerificationStep("details"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName) {
      dispatch(setError("Please fill in your full name"));
      return;
    }

    if (formData.hasLicense && !formData.licenseNumber) {
      dispatch(setError("Please enter your license number"));
      return;
    }

    if (formData.hasLicense && !formData.licenseFile) {
      dispatch(setError("Please upload your license document"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    const agentData = {
      full_name: String(formData.fullName).trim(),
    };

    if (formData.hasLicense) {
      agentData.license_number = String(formData.licenseNumber).trim();
      // Include the license file if it exists
      if (formData.licenseFile) {
        agentData.licenseFile = formData.licenseFile;
      }
    }

    // Add agency_id if this is an invitation flow (ensure it's an integer)
    if (invitationContext.isInvitationFlow && invitationContext.agencyId) {
      agentData.agency_id = parseInt(invitationContext.agencyId, 10);
    }

    console.log("Submitting agent data:", agentData);

    try {
      // First create the agent account
      const response = await authService.createAgent(agentData);

      if (response.success) {
        console.log("Agent creation successful, updating auth state");

        // If agent selected an agency, send join request after account creation
        if (selectedAgency) {
          try {
            const agencyResponse = await agentService.createJoinRequest(
              selectedAgency.id
            );
            if (agencyResponse.success) {
              toast.success(
                `Agent account created successfully! Request sent to ${selectedAgency.name}. Pending agency approval.`
              );
            } else {
              // Account created but agency request failed
              toast.success("Agent account created successfully!");
              toast.error(
                `Failed to send request to ${selectedAgency.name}: ${agencyResponse.error}`
              );
            }
          } catch (agencyError) {
            // Account created but agency request failed with exception
            console.error("Agency join request error:", agencyError);
            toast.success("Agent account created successfully!");
            toast.error(
              `Failed to send request to ${selectedAgency.name}. Please try again from your profile.`
            );
          }
        } else {
          toast.success("Agent account created successfully!");
        }

        dispatch(setSignupData({ agentDetails: agentData }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(setError(response.error || "Failed to create agent"));
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6">
      <div className="flex items-center mb-4">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-center justify-center w-12 h-12 bg-[#374DF5] rounded-2xl mb-4">
          <svg
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 13C12.7614 13 15 10.7614 15 8C15 5.23858 12.7614 3 10 3C7.23858 3 5 5.23858 5 8C5 10.7614 7.23858 13 10 13Z"
              stroke="white"
              stroke-width="1.04167"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M6.66536 11.9166C5.67114 12.3882 4.83058 13.1314 4.24075 14.0604C3.65093 14.9893 3.33589 16.0662 3.33203 17.1666C3.33203 17.3876 3.41983 17.5996 3.57611 17.7559C3.73239 17.9121 3.94435 17.9999 4.16536 17.9999H15.832C16.053 17.9999 16.265 17.9121 16.4213 17.7559C16.5776 17.5996 16.6654 17.3876 16.6654 17.1666C16.6662 16.062 16.3534 14.9798 15.7633 14.0459C15.1732 13.1121 14.3301 12.365 13.332 11.8916"
              stroke="white"
              stroke-width="1.04167"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
          Setup Agent
        </h2>
        <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
          Have an agency?
          <button
            onClick={handleCreateAgencyAccount}
            className="text-[#2283FF] hover:text-[#1a6cd4] ml-1 font-medium"
          >
            Create Agency account
          </button>
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              Full name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="James Whither"
              value={formData.fullName}
              onChange={(e) => handleInputChange("fullName", e.target.value)}
              required
            />
          </div>

          {/* License Checkbox */}
          <div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="hasLicense"
                checked={formData.hasLicense}
                onChange={(e) => handleLicenseCheckboxChange(e.target.checked)}
                className="h-4 w-4 text-[#2283FF] focus:ring-[#2283FF] border-gray-300 rounded"
              />
              <label
                htmlFor="hasLicense"
                className="ml-2 block text-sm font-medium text-black"
              >
                I have a license
              </label>
            </div>
          </div>

          {/* Conditional License Fields */}
          {formData.hasLicense && (
            <>
              <div>
                <label className="block font-medium text-[#000000] text-base mb-1">
                  License number*
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                  placeholder="202401-1234"
                  value={formData.licenseNumber}
                  onChange={(e) =>
                    handleInputChange("licenseNumber", e.target.value)
                  }
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Upload License Document*
                </label>
                {!formData.licenseFile ? (
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      id="license-file-upload"
                      onChange={handleFileChange}
                      className="hidden"
                      accept={LICENSE_FILE_ACCEPT}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        document.getElementById("license-file-upload")?.click()
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          document
                            .getElementById("license-file-upload")
                            ?.click();
                        }
                      }}
                      className="inline-flex items-center mt-1 px-3 py-2 text-sm font-medium text-white bg-[#2283FF] rounded-md hover:bg-[#1a6cd4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2283FF]"
                      aria-label="Upload license document"
                      tabIndex="0"
                    >
                      Upload license
                    </button>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg
                            className="w-5 h-5 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formData.licenseFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(formData.licenseFile.size / 1024 / 1024).toFixed(
                              2
                            )}{" "}
                            MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Agency Selection */}
          <div>
            <label className="block font-medium text-[#000000] text-base mb-1">
              Join an Agency (Optional)
            </label>
            <div className="border border-gray-300 rounded-lg h-12 flex items-center">
              {selectedAgency ? (
                <div className="flex items-center justify-between w-full px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center">
                      {selectedAgency.logo_url && (
                        <img
                          src={selectedAgency.logo_url}
                          alt={selectedAgency.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      )}
                    </div>
                    <span className="text-base text-gray-900 font-medium">
                      {selectedAgency.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedAgency(null)}
                    className="text-secondary hover:text-gray-900"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAgencySearchModal(true)}
                  className="w-full h-full px-4 text-left"
                >
                  <span className="text-base text-gray-500">
                    Search for an agency to join...
                  </span>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedAgency
                ? `You will send a join request to ${selectedAgency.name} when you create your account.`
                : "You can join an agency now or later from your profile"}
            </p>
          </div>

          <button
            type="submit"
            disabled={
              loading ||
              !formData.fullName ||
              (formData.hasLicense &&
                (!formData.licenseNumber || !formData.licenseFile))
            }
            className={`w-full py-3 px-4 rounded-lg transition-colors font-medium ${
              loading ||
              !formData.fullName ||
              (formData.hasLicense &&
                (!formData.licenseNumber || !formData.licenseFile))
                ? "bg-[#2283FF1A] text-[#2283FF] cursor-not-allowed"
                : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
            }`}
          >
            {loading
              ? "Creating account..."
              : selectedAgency
              ? `Create account & send request to ${selectedAgency.name}`
              : "Create account"}
          </button>
        </form>
      </div>

      {/* Agency Search Modal */}
      <AgencySearchModal
        isOpen={showAgencySearchModal}
        onClose={() => setShowAgencySearchModal(false)}
        onAgencySelect={handleAgencySelect}
        title="Search Agencies to Join"
      />
    </div>
  );
};

export default AgentDetailsStep;
